import React, { useState } from 'react';
import { Table, Tag, Icon, Input } from 'antd';

const { Search } = Input;

// Map endpoint paths to friendly feature names
const getFeatureName = (path) => {
  if (!path) return '-';
  const pathLower = path.toLowerCase();
  
  if (pathLower.includes('/openai/chat/completions')) return 'AI Insights';
  if (pathLower.includes('/survey-generation/suggest-questions')) return 'Smart Survey';
  if (pathLower.includes('/survey-generation')) return 'Survey Generation';
  if (pathLower.includes('/admin/metrics')) return 'Admin Metrics';
  if (pathLower.includes('/health')) return 'Health Check';
  
  // Return last segment of path as fallback
  const segments = path.split('/').filter(s => s);
  return segments[segments.length - 1] || path;
};

const RecentRequestsTable = ({ data, loading }) => {
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState(data);

  // Filter data based on search text
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredData(data);
    } else {
      const filtered = data.filter(record => 
        Object.values(record).some(field => 
          String(field).toLowerCase().includes(value.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
  };

  // Update filtered data when data prop changes
  React.useEffect(() => {
    if (!searchText) {
      setFilteredData(data);
    } else {
      handleSearch(searchText);
    }
  }, [data, searchText]);

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      sorter: (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0),
      render: (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleString();
      },
    },
    {
      title: 'Feature',
      dataIndex: 'path',
      key: 'feature',
      width: 130,
      sorter: (a, b) => getFeatureName(a.path).localeCompare(getFeatureName(b.path)),
      render: (path) => {
        const featureName = getFeatureName(path);
        const color = featureName === 'AI Insights' ? 'blue' : 
                     featureName === 'Smart Survey' ? 'green' : 'default';
        return <Tag color={color}>{featureName}</Tag>;
      },
    },
    {
      title: 'Request',
      dataIndex: 'request_data',
      key: 'request_data',
      width: 180,
      render: (requestData, record) => {
        const raw = requestData || record.request_snippet || record.query || record.request || record.input || '-';
        const str = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
        return (
          <div title={str} style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {str}
          </div>
        );
      },
    },
    {
      title: 'Response',
      dataIndex: 'response_data',
      key: 'response_data',
      width: 180,
      render: (responseData, record) => {
        const raw = responseData || record.response_snippet || record.response || record.output || record.result || '-';
        const str = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
        return (
          <div title={str} style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {str}
          </div>
        );
      },
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 120,
      sorter: (a, b) => (a.model || '').localeCompare(b.model || ''),
      render: (model, record) => {
        // Try multiple possible field names for model
        const modelValue = model || record.model_name || record.ai_model || record.llm_model || '-';
        return modelValue;
      },
    },
    {
      title: 'Tokens',
      dataIndex: 'tokens',
      key: 'tokens',
      width: 90,
      sorter: (a, b) => (a.tokens || 0) - (b.tokens || 0),
      render: (tokens, record) => {
        const tokenValue = tokens || record.tokens_used || record.total_tokens || 0;
        return tokenValue > 0 ? tokenValue.toLocaleString() : '-';
      },
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: 90,
      sorter: (a, b) => (a.cost || 0) - (b.cost || 0),
      render: (cost, record) => {
        const costValue = cost || record.cost_estimate || record.estimated_cost || 0;
        return costValue > 0 ? `$${costValue.toFixed(4)}` : '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: (a, b) => {
        const statusA = a.status || a.status_code || a.response_status || 'unknown';
        const statusB = b.status || b.status_code || b.response_status || 'unknown';
        return String(statusA).localeCompare(String(statusB));
      },
      render: (status, record) => {
        const statusValue = status || record.status_code || record.response_status || 'unknown';
        const color = statusValue === 200 || statusValue === '200' || statusValue === 'success' ? 'green' : 'red';
        const icon = statusValue === 200 || statusValue === '200' || statusValue === 'success' ? 'check-circle' : 'close-circle';
        
        return (
          <Tag color={color}>
            <Icon type={icon} />
            {statusValue}
          </Tag>
        );
      },
    },
    {
      title: 'User Email',
      dataIndex: 'user_email',
      key: 'user_email',
      width: 180,
      sorter: (a, b) => {
        const emailA = a.user_email || a.user || a.user_id || a.username || '';
        const emailB = b.user_email || b.user || b.user_id || b.username || '';
        return emailA.localeCompare(emailB);
      },
      render: (userEmail, record) => {
        // Prioritize email fields, then fallback to other user identifiers
        const userValue = userEmail || record.email || record.user_email || 
                         record.user || record.user_id || record.username;
        return userValue || '-';
      },
    },
    {
      title: 'Response Time',
      dataIndex: 'response_time',
      key: 'response_time',
      width: 120,
      sorter: (a, b) => (a.response_time || 0) - (b.response_time || 0),
      render: (time) => time ? `${time}ms` : '-',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search requests by any field..."
          allowClear
          enterButton="Search"
          size="default"
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
      </div>
      {/* Top scrollbar for horizontal scroll */}
      <div 
        style={{ 
          overflowX: 'auto', 
          overflowY: 'hidden',
          marginBottom: 8
        }}
        onScroll={(e) => {
          // Sync scroll with table
          const tableContainer = e.target.nextElementSibling?.querySelector('.ant-table-body');
          if (tableContainer) {
            tableContainer.scrollLeft = e.target.scrollLeft;
          }
        }}
      >
        <div style={{ width: 1200, height: 1 }} />
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey={(record, index) => record.id || index}
        pagination={{
          pageSize: 25,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
        }}
        scroll={{ x: 1200, scrollToFirstRowOnChange: true }}
        sticky={{ offsetHeader: 0 }}
        size="small"
        onScroll={(e) => {
          // Sync scroll with top scrollbar
          const topScroll = e.currentTarget.parentElement?.querySelector('div[style*="overflowX"]');
          if (topScroll && e.currentTarget.querySelector('.ant-table-body')) {
            topScroll.scrollLeft = e.currentTarget.querySelector('.ant-table-body').scrollLeft;
          }
        }}
      />
    </div>
  );
};

export default RecentRequestsTable;
